import { GameBaseScene } from './game-base-scene';

export type GameSizeMetrics = {
  widthGame: number;
  heightGame: number;
  widthDpr: number;
  heightDpr: number;
  dpr: number;
};

export abstract class ScaledBaseScene extends GameBaseScene {
  private currentDpr = 1;
  private isGameVisible = false;

  public get currentGameSizeMetrics() {
    if (!this.game || !this.game.scale) {
      return {
        widthGame: 0,
        heightGame: 0,
        widthDpr: 0,
        heightDpr: 0,
        dpr: 1,
      };
    }

    const dpr = window.devicePixelRatio;
    const containerWidth = this.game.scale.canvas.parentElement?.offsetWidth ?? 0;
    const containerHeight = this.game.scale.canvas.parentElement?.offsetHeight ?? 0;

    return {
      widthGame: containerWidth,
      heightGame: containerHeight,
      widthDpr: containerWidth * dpr,
      heightDpr: containerHeight * dpr,
      dpr: dpr,
    };
  }

  public setGameVisible(isVisible: boolean): void {
    this.isGameVisible = isVisible;

    if (isVisible) {
      // Resize the screen once again when entering, should the browser dimensions change
      // if the game is hidden, the view dimensions can be zero
      const gameSizeMetrics = this.resizeScene();

      this.onResize(gameSizeMetrics);
    }
  }

  protected override initGraphics() {
    super.initGraphics();

    this.resizeScene();
  }

  protected override onResizeEvent() {
    super.onResizeEvent();

    if (!this.isGameVisible) {
      return;
    }

    const gameSizeMetrics = this.resizeScene();
    this.onResize(gameSizeMetrics);
  }

  protected abstract onResize(_gameSizeMetrics: GameSizeMetrics): void;

  private resizeScene(): GameSizeMetrics {
    if (!this.game || !this.game.scale) {
      return {
        widthGame: 0,
        heightGame: 0,
        widthDpr: 0,
        heightDpr: 0,
        dpr: 1,
      };
    }

    this.currentDpr = window.devicePixelRatio;
    if (this.currentDpr === 0) {
      this.currentDpr = 1;
    }

    const containerWidth = this.game.scale.canvas.parentElement?.parentElement?.offsetWidth ?? 0;
    const containerHeight = this.game.scale.canvas.parentElement?.parentElement?.offsetHeight ?? 0;

    if (containerWidth === 0 || containerHeight === 0) {
      return {
        widthGame: 0,
        heightGame: 0,
        widthDpr: 0,
        heightDpr: 0,
        dpr: 1,
      };
    }

    const widthGame = containerWidth;
    const heightGame = containerHeight;

    const widthDpr = widthGame * this.currentDpr;
    const heightDpr = heightGame * this.currentDpr;

    if (this.game.scale.parent instanceof HTMLDivElement) {
      this.game.scale.parent.style.width = `${Math.round(widthGame)}px`;
      this.game.scale.parent.style.height = `${Math.round(heightGame)}px`;
    }

    this.game.scale.canvas.width = widthDpr;
    this.game.scale.canvas.height = heightDpr;

    this.game.scale.canvas.style.width = `${widthGame}px`;
    this.game.scale.canvas.style.height = `${heightGame}px`;

    this.cameras.main.setBounds(0, 0, widthDpr, heightDpr);
    this.cameras.main.setSize(widthDpr, heightDpr);
    this.cameras.main.setViewport(0, 0, widthDpr, heightDpr);

    this.scale.setGameSize(widthDpr, heightDpr);

    return {
      widthGame: widthGame,
      heightGame: heightGame,
      widthDpr: widthDpr,
      heightDpr: heightDpr,
      dpr: this.currentDpr,
    };
  }
}
